import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Calendar,
  User,
  MapPin,
  Edit,
  Trash2,
  RefreshCw,
  Filter,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { format, addDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function MemberRequestsModule() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Edit form states
  const [editDate, setEditDate] = useState("");
  const [editTime, setEditTime] = useState("");
  const [editEndTime, setEditEndTime] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // Generate time slots
  const timeSlots = [];
  for (let h = 6; h < 23; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeSlots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  // Generate date options (next 14 days)
  const dateOptions = [];
  for (let i = 0; i < 14; i++) {
    const date = addDays(new Date(), i);
    dateOptions.push({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "d MMMM yyyy, EEEE", { locale: getDateLocale() })
    });
  }

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await fetch(`${API}/member-dashboard/requests`, {
        credentials: "include",
      });
      if (response.ok) {
        setRequests(await response.json());
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: t('common:pending'), color: "bg-orange-100 text-orange-600", icon: Clock },
      approved: { label: t('admin:approved'), color: "bg-green-100 text-green-600", icon: CheckCircle2 },
      rejected: { label: t('admin:rejected'), color: "bg-red-100 text-red-600", icon: XCircle },
      cancelled: { label: t('common:cancelled'), color: "bg-grey-100 text-grey-600", icon: AlertCircle },
    };
    const s = statusMap[status] || statusMap.pending;
    const Icon = s.icon;
    return (
      <Badge className={`${s.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {s.label}
      </Badge>
    );
  };

  const filteredRequests = requests.filter((r) => {
    if (statusFilter === "all") return true;
    return r.status === statusFilter;
  });

  const openEditModal = (request) => {
    setSelectedRequest(request);
    setEditDate(request.date);
    setEditTime(request.start_time);
    setEditEndTime(request.end_time || "");
    setEditNotes(request.notes || "");
    setShowEditModal(true);
  };

  const openCancelModal = (request) => {
    setSelectedRequest(request);
    setCancelReason("");
    setShowCancelModal(true);
  };

  const handleUpdateRequest = async () => {
    if (!editDate || !editTime) {
      toast.error("Tarih ve saat zorunludur");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/members/me/reservation-requests/${selectedRequest.request_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            date: editDate,
            start_time: editTime,
            end_time: editEndTime,
            notes: editNotes,
          }),
        }
      );

      if (response.ok) {
        toast.success(t('common:requestUpdated'));
        setShowEditModal(false);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('settings:updateFailed'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${API}/members/me/reservation-requests/${selectedRequest.request_id}/cancel`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ reason: cancelReason }),
        }
      );

      if (response.ok) {
        toast.success(t('common:requestCancelled'));
        setShowCancelModal(false);
        fetchRequests();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("common:cancelFailed"));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  // Stats
  const stats = {
    total: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    approved: requests.filter((r) => r.status === "approved").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy">Taleplerim</h2>
          <p className="text-grey text-sm">Rezervasyon taleplerinizi takip edin</p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder={t('common:status')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü ({stats.total})</SelectItem>
              <SelectItem value="pending">Beklemede ({stats.pending})</SelectItem>
              <SelectItem value="approved">Onaylanan ({stats.approved})</SelectItem>
              <SelectItem value="rejected">Reddedilen ({stats.rejected})</SelectItem>
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={fetchRequests}
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">{stats.total}</p>
                <p className="text-xs text-grey">Toplam Talep</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-orange-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-50 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{stats.pending}</p>
                <p className="text-xs text-grey">{t('common:pending')}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-green-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                <p className="text-xs text-grey">Onaylanan</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm border-l-4 border-l-red-400">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                <p className="text-xs text-grey">Reddedilen</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requests List */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Talep Listesi</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredRequests.length > 0 ? (
            <div className="space-y-3">
              {filteredRequests.map((req) => (
                <div
                  key={req.request_id}
                  className={`p-4 rounded-xl border ${
                    req.status === "pending"
                      ? "bg-orange-50 border-orange-200"
                      : req.status === "approved"
                      ? "bg-green-50 border-green-200"
                      : req.status === "rejected"
                      ? "bg-red-50 border-red-200"
                      : "bg-slate-50 border-slate-200"
                  }`}
                  data-testid={`request-${req.request_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(req.status)}
                        <span className="text-sm text-grey">
                          {format(new Date(req.created_at), "d MMM yyyy HH:mm", { locale: getDateLocale() })}
                        </span>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 font-medium text-navy">
                          <Calendar className="w-4 h-4" />
                          {req.date} {req.start_time} - {req.end_time || "-"}
                        </span>
                        {req.trainer_name && (
                          <span className="flex items-center gap-1 text-grey">
                            <User className="w-4 h-4" />
                            {req.trainer_name}
                          </span>
                        )}
                        {req.room_name && (
                          <span className="flex items-center gap-1 text-grey">
                            <MapPin className="w-4 h-4" />
                            {req.room_name}
                          </span>
                        )}
                      </div>
                      
                      {req.branch && (
                        <Badge variant="secondary" className="text-xs">
                          {getLocalizedBranch(req.branch)}
                        </Badge>
                      )}
                      
                      {req.notes && (
                        <p className="text-sm text-grey italic">Not: {req.notes}</p>
                      )}
                      
                      {/* Rejection reason */}
                      {req.status === "rejected" && req.reject_reason && (
                        <div className="p-2 bg-red-100 rounded-lg text-sm text-red-700">
                          <strong>Red nedeni:</strong> {req.reject_reason}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions - only for pending */}
                    {req.status === "pending" && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl"
                          onClick={() => openEditModal(req)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Değiştir
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => openCancelModal(req)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          İptal
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-grey">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{t("common:talepBulunamadi")}</p>
              {statusFilter !== "all" && (
                <Button
                  variant="link"
                  onClick={() => setStatusFilter("all")}
                  className="mt-2"
                >
                  Tüm talepleri göster
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:talebiDuzenle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t('common:date')}</Label>
              <Select value={editDate} onValueChange={setEditDate}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue placeholder={t('common:selectDate')} />
                </SelectTrigger>
                <SelectContent>
                  {dateOptions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t('settings:startTime')}</Label>
                <Select value={editTime} onValueChange={setEditTime}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t('common:time')} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('settings:endTime')}</Label>
                <Select value={editEndTime} onValueChange={setEditEndTime}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder={t('common:time')} />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>{t('common:note')}</Label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Ek notlar..."
                className="rounded-xl"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowEditModal(false)}
              >
                Vazgeç
              </Button>
              <Button
                className="flex-1 bg-navy hover:bg-navy/90 rounded-xl"
                onClick={handleUpdateRequest}
                disabled={submitting}
              >
                {submitting ? "Kaydediliyor..." : t('common:save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">{t("common:talebiİptalEt")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedRequest && (
              <div className="p-3 bg-slate-50 rounded-xl text-sm">
                <p><strong>Tarih:</strong> {selectedRequest.date} {selectedRequest.start_time}</p>
                {selectedRequest.trainer_name && (
                  <p><strong>Antrenör:</strong> {selectedRequest.trainer_name}</p>
                )}
              </div>
            )}
            
            <div>
              <Label>İptal Nedeni (opsiyonel)</Label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Neden iptal ediyorsunuz?"
                className="rounded-xl"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={() => setShowCancelModal(false)}
              >
                Vazgeç
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 rounded-xl"
                onClick={handleCancelRequest}
                disabled={submitting}
              >
                {submitting ? t("common:requestCancelling") : t("common:cancelRequest")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
